/**
 * RecursiveSkillEffect — Conjuration Tether path passive: shade critical hits.
 *
 * Always-on passive. One in ten shade attacks crits — double damage plus a
 * 0.3-second stagger on the hit target. Each shade (whether from Tether,
 * Mirror, or Fracture) rolls its crit chance independently per attack.
 *
 * Integration hook:
 *   - `rollShadeCrit(shadeDamage)` — called by the integration layer on each
 *     shade attack. Returns a `ShadeCritResult` containing the final damage
 *     (crit or normal) and whether the crit fired.
 *   - When a crit fires, the caller must apply the stagger via
 *     `getResolvedStaggerMs()` → StatusEffectSystem.apply('stagger', { durationMs }).
 *     The integration layer handles immunity windows — this effect only
 *     reports "crit happened, stagger this long." The CC hierarchy (ADR-065)
 *     is enforced by StatusEffectSystem, not here.
 *
 * UpgradeEffectResolver keys (all tree-scoped to 'conjuration'):
 * - shadeCritChance: additive, base 0.1 — so future crit-chance nodes stack
 * - shadeCritMultiplier: multiplicative (product), base 2.0
 * - shadeCritStaggerMs: additive, base 300
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see conjuration-spec.md § Recursive
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShadeCritResult {
  /** Final damage to deal (crit-multiplied or unchanged). */
  readonly damage: number;
  /** Whether the crit fired. */
  readonly isCrit: boolean;
  /** Stagger duration in ms (0 if no crit). */
  readonly staggerMs: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RecursiveDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  /** Injectable RNG for deterministic testing. Defaults to Math.random. */
  readonly random?: () => number;
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface RecursiveConfig {
  /** Base crit chance per shade attack (0–1). */
  readonly shadeCritChance: number;
  /** Base crit damage multiplier. */
  readonly shadeCritMultiplier: number;
  /** Base stagger duration in ms on crit. */
  readonly shadeCritStaggerMs: number;
}

const DEFAULT_CONFIG: RecursiveConfig = {
  shadeCritChance: 0.1,          // spec § Recursive: 10%
  shadeCritMultiplier: 2.0,      // spec § Recursive: double damage
  shadeCritStaggerMs: 300,       // spec § Recursive: 300ms stagger
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RecursiveSkillEffect implements SkillEffect {
  readonly skillId = 'tether_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RecursiveDeps;
  private readonly config: RecursiveConfig;
  private readonly rng: () => number;

  // Observable state for scenarios
  private _totalCrits = 0;
  private _totalRolls = 0;

  constructor(deps: RecursiveDeps, config: Partial<RecursiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.rng = deps.random ?? Math.random;
  }

  // --- Recursive-specific API ---

  /**
   * Resolve the effective crit chance. Additive stacking: base 0.1 plus
   * any bonus from future crit-chance nodes.
   * Clamped to [0, 1].
   */
  getResolvedCritChance(): number {
    const bonus = this.deps.resolver.getAdditiveEffect('tether_path_passive', 'shadeCritChance');
    return Math.max(0, Math.min(1, this.config.shadeCritChance + bonus));
  }

  /**
   * Resolve the effective crit multiplier. Multiplicative stacking: base 2.0
   * multiplied by product-aggregated bonuses.
   */
  getResolvedCritMultiplier(): number {
    const product = this.deps.resolver.getMultiplicativeEffect('tether_path_passive', 'shadeCritMultiplier');
    return this.config.shadeCritMultiplier * (product === 0 ? 1 : product);
  }

  /**
   * Resolve the effective stagger duration in ms. Additive stacking:
   * base 300 plus bonuses from future stagger-extending nodes.
   * Clamped to a minimum of 0.
   */
  getResolvedStaggerMs(): number {
    const bonus = this.deps.resolver.getAdditiveEffect('tether_path_passive', 'shadeCritStaggerMs');
    return Math.max(0, this.config.shadeCritStaggerMs + bonus);
  }

  /**
   * Roll a shade crit for a single shade attack. Called by the integration
   * layer on each `applyShadeHit()` call. Each shade rolls independently.
   *
   * @param shadeDamage  The shade's resolved base damage for this attack
   * @returns ShadeCritResult with final damage, crit flag, and stagger duration
   */
  rollShadeCrit(shadeDamage: number): ShadeCritResult {
    this._totalRolls++;

    const critChance = this.getResolvedCritChance();
    const roll = this.rng();

    if (roll < critChance) {
      this._totalCrits++;
      const multiplier = this.getResolvedCritMultiplier();
      return {
        damage: shadeDamage * multiplier,
        isCrit: true,
        staggerMs: this.getResolvedStaggerMs(),
      };
    }

    return {
      damage: shadeDamage,
      isCrit: false,
      staggerMs: 0,
    };
  }

  // --- State getters ---

  get totalCrits(): number { return this._totalCrits; }
  get totalRolls(): number { return this._totalRolls; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — no frame logic
  }

  destroy(): void {
    // No resources to release
  }
}
